import React, { useEffect } from 'react';
import { Form, Input, Space, Typography, Alert, App } from 'antd';
import { trpc } from '../../lib/api';
import { QuantityStepperInput, withDevModalFilePath } from '../common';

const { TextArea } = Input;
const { Text } = Typography;
const { Modal } = withDevModalFilePath('client/src/components/parts/StockAdjustmentModal.tsx');

interface Part {
  id?: string;
  name?: string;
  displayId?: string;
  currentStockLevel?: number | null;
  unit?: string | null;
}

interface StockAdjustmentModalProps {
  open: boolean;
  part: Part | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface AdjustmentFormValues {
  newStockLevel: number;
  reason: string;
}

export const StockAdjustmentModal: React.FC<StockAdjustmentModalProps> = ({
  open,
  part,
  onClose,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<AdjustmentFormValues>();

  // 棚卸mutation
  const adjustMutation = trpc.partStockLogs.adjustStock.useMutation({
    onSuccess: () => {
      message.success('棚卸を完了しました');
      form.resetFields();
      onSuccess();
    },
    onError: (error) => {
      message.error(`棚卸に失敗しました: ${error.message}`);
    },
  });

  // フォーム初期値設定
  useEffect(() => {
    if (open && part) {
      form.setFieldsValue({
        newStockLevel: part.currentStockLevel ?? 0,
        reason: '',
      });
    }
  }, [open, part, form]);

  // 送信処理
  const handleSubmit = async () => {
    if (!part) return;

    try {
      const values = await form.validateFields();

      adjustMutation.mutate({
        partId: part.id,
        ...values,
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const currentStock = part?.currentStockLevel ?? 0;
  const watchNewStock = Form.useWatch('newStockLevel', form);
  const difference = watchNewStock !== undefined ? watchNewStock - currentStock : 0;

  return (
    <>
      <Modal
        title={`棚卸 - ${part?.name || ''}`}
        open={open}
        onCancel={onClose}
        onOk={handleSubmit}
        confirmLoading={adjustMutation.isPending}
        width={600}
        okText="棚卸実行"
        cancelText="キャンセル"
      >
        {part && (
          <>
            <Alert
              message="在庫調整"
              description="実際の在庫数を入力してください。システムの在庫数が更新されます。"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text strong>現在のシステム在庫数: </Text>
                <Text type="warning" style={{ fontSize: 18 }}>
                  {currentStock} {part.unit ?? '個'}
                </Text>
              </div>
            </Space>

            <Form
              form={form}
              layout="vertical"
              style={{ marginTop: 24 }}
              initialValues={{
                newStockLevel: currentStock,
              }}
            >
              <Form.Item
                label="実際の在庫数（棚卸結果）"
                name="newStockLevel"
                rules={[
                  { required: true, message: '在庫数を入力してください' },
                  { type: 'number', min: 0, message: '0以上の値を入力してください' },
                ]}
              >
                <QuantityStepperInput
                  style={{ width: '100%' }}
                  min={0}
                  addonAfter={part.unit ?? '個'}
                  size="large"
                  aria-label={`${part.name ?? '部品・消耗品'}の実際の在庫数`}
                  incrementAriaLabel={`${part.name ?? '部品・消耗品'}の実際の在庫数を1増やす`}
                />
              </Form.Item>

              {difference !== 0 && (
                <Alert
                  message={
                    <Space>
                      <Text>差分: </Text>
                      <Text strong style={{ color: difference > 0 ? '#52c41a' : '#ff4d4f' }}>
                        {difference > 0 ? '+' : ''}{difference} {part.unit ?? '個'}
                      </Text>
                    </Space>
                  }
                  type={difference > 0 ? 'success' : 'warning'}
                  style={{ marginBottom: 16 }}
                />
              )}

              <Form.Item
                label="棚卸理由"
                name="reason"
                rules={[{ required: true, message: '棚卸理由を入力してください' }]}
              >
                <TextArea
                  rows={3}
                  placeholder="例: 月次棚卸、破損による廃棄、実地調査"
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </>
  );
};

export default StockAdjustmentModal;
